import { useEffect, useMemo, useRef, useState } from "react";

import { Skeleton } from "../ui/skeleton";
import { GifType, Wrangler } from "../ui/Wrangler";

import ChangelogItem from "./ChangelogItem";
import FilterBar, { ChangelogFilterState } from "./FilterBar";

interface ChangelogContentProps {
  data?: unknown;
  pendingData?: unknown;
  isLoading: boolean;
  error: unknown;
  fetchNextPage: () => void;
  hasNextPage?: boolean;
  isFetchingNextPage: boolean;
  filters: ChangelogFilterState;
  onFiltersChange: (filters: ChangelogFilterState) => void;
  emptyStateMessage?: string;
  scrollToId?: string | null;
  onClearScrollTo?: () => void;
}

const SkeletonItem = () => (
  <div className="relative">
    <div className="flex gap-4">
      <div className="flex-shrink-0">
        <Skeleton width={32} height={32} borderRadius="9999px" />
      </div>
      <div className="flex-1">
        <Skeleton width="100%" height={96} borderRadius="0.5rem" />
      </div>
    </div>
  </div>
);

const ChangelogContent = ({
  data,
  pendingData,
  isLoading,
  error,
  fetchNextPage,
  hasNextPage,
  isFetchingNextPage,
  filters,
  onFiltersChange,
  emptyStateMessage = "Changelog events are tracked for new activities. Legacy entities may not have historical events recorded.",
  scrollToId,
  onClearScrollTo,
}: ChangelogContentProps) => {
  const observerTarget = useRef<HTMLDivElement>(null);
  const scrollTargetRef = useRef<HTMLDivElement>(null);
  const hasScrolledRef = useRef(false);
  const [highlightedId, setHighlightedId] = useState<number | null>(null);

  const pendingCount = useMemo(() => {
    const pages = (pendingData as { pages?: { total?: number }[] })?.pages;
    return pages?.[0]?.total;
  }, [pendingData]);

  const allEvents = useMemo(() => {
    const pages = (data as { pages?: { items: { id: number }[] }[] })?.pages;
    return pages?.flatMap(page => page.items) ?? [];
  }, [data]);

  useEffect(() => {
    const currentTarget = observerTarget.current;

    const observer = new IntersectionObserver(
      entries => {
        if (entries[0].isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage();
        }
      },
      { threshold: 0.1 },
    );

    if (currentTarget) {
      observer.observe(currentTarget);
    }

    return () => {
      if (currentTarget) {
        observer.unobserve(currentTarget);
      }
    };
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  useEffect(() => {
    if (!pendingCount && filters.showPendingOnly) {
      onFiltersChange({ ...filters, showPendingOnly: false });
    }
  }, [pendingCount, filters.showPendingOnly, onFiltersChange, filters]);

  // Handle scrollTo functionality
  useEffect(() => {
    if (scrollToId && !hasScrolledRef.current && !isLoading && allEvents.length > 0) {
      const targetId = Number(scrollToId);
      const targetExists = allEvents.some(event => event.id === targetId);

      if (targetExists) {
        hasScrolledRef.current = true;
        setHighlightedId(targetId);

        if (onClearScrollTo) {
          onClearScrollTo();
        }

        setTimeout(() => {
          const element = document.getElementById(`changelog-item-${targetId}`);
          if (element) {
            element.scrollIntoView({ behavior: "smooth", block: "center" });
          }
        }, 100);

        setTimeout(() => {
          setHighlightedId(null);
        }, 3000);
      }
    }
  }, [scrollToId, isLoading, allEvents, onClearScrollTo]);

  // Reset scroll flag when scrollToId changes
  useEffect(() => {
    if (scrollToId) {
      hasScrolledRef.current = false;
    }
  }, [scrollToId]);

  if (isLoading) {
    return (
      <>
        <FilterBar
          filters={filters}
          onFiltersChange={onFiltersChange}
          pendingCount={pendingCount}
        />
        <div className="w-full space-y-4">
          {[...Array(3)].map((_, index) => (
            // eslint-disable-next-line react/no-array-index-key
            <SkeletonItem key={index} />
          ))}
        </div>
      </>
    );
  }

  if (error) {
    return (
      <>
        <FilterBar
          filters={filters}
          onFiltersChange={onFiltersChange}
          pendingCount={pendingCount}
        />
        <div className="flex items-center justify-center py-8">
          <p className="text-sm text-destructive">Error loading changelog</p>
        </div>
      </>
    );
  }

  if (allEvents.length === 0) {
    return (
      <>
        <FilterBar
          filters={filters}
          onFiltersChange={onFiltersChange}
          pendingCount={pendingCount}
        />
        <div className="flex flex-col items-center justify-center py-16">
          <Wrangler variant={GifType.Searching} size="lg" hideText />
          <h3 className="mb-2 text-sm font-medium text-gray-900">No changelog events yet</h3>
          <p className="max-w-sm text-center text-sm text-gray-500">{emptyStateMessage}</p>
        </div>
      </>
    );
  }

  return (
    <>
      <FilterBar filters={filters} onFiltersChange={onFiltersChange} pendingCount={pendingCount} />
      <div className="w-full space-y-4">
        {allEvents.map((event, index) => (
          <div
            key={event.id}
            id={`changelog-item-${event.id}`}
            ref={event.id === Number(scrollToId) ? scrollTargetRef : undefined}
            className={`transition-all duration-500 ${
              highlightedId === event.id
                ? "rounded-lg bg-amber-50/50 p-3 shadow-[0_0_0_2px_rgb(251,191,36)]"
                : ""
            }`}
          >
            <ChangelogItem event={event as never} isLast={index === allEvents.length - 1} />
          </div>
        ))}

        {isFetchingNextPage && (
          <div ref={observerTarget}>
            <SkeletonItem />
          </div>
        )}

        {hasNextPage && !isFetchingNextPage && <div ref={observerTarget} className="h-4" />}
      </div>
    </>
  );
};

export default ChangelogContent;
